import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppConfigService, Page } from '@markant-platform/shared/data-access';
import { Observable } from 'rxjs';
import { OrganizationDto } from '@markant-platform/shared/organization/model';
import { CartridgeDto, UsageRight, UsageRightActivationState } from '@markant-platform/usage-right/model';
import { BackofficeBookingState } from '@markant-platform/shared/backoffice/model';
import { OrganizationDetailData } from 'data-model';
import { BookingState, ServiceUpgradeState } from '@markant-platform/cartridge/model';

@Injectable({
  providedIn: 'root',
})
export class UsageRightService {
  constructor(private http: HttpClient) {}

  getUsageRightsForOrganization(
    organizationId: string,
    pageIndex: number,
    pageSize: number, //set to 0, to get all Usage-Rights
    filterQuery?: string
  ): Observable<Page<UsageRight>> {
    return this.http.get<Page<UsageRight>>(
      `${
        AppConfigService.appConfiguration.apiUrl['usageRightServiceOrganization']
      }/${organizationId}?index=${pageIndex}&size=${pageSize}${filterQuery ? '&' + filterQuery : ''}`
    );
  }

  getUsageRightsForCurrentOrganization(
    pageIndex: number,
    pageSize: number,
    filterQuery?: string
  ): Observable<Page<UsageRight>> {
    return this.http.get<Page<UsageRight>>(
      `${
        AppConfigService.appConfiguration.apiUrl['usageRightServiceOrganization']
      }/current?index=${pageIndex}&size=${pageSize}${filterQuery ? '&' + filterQuery : ''}`
    );
  }

  getUsageRightForOrganizationByBookingId(organizationId: string, bookingId: string): Observable<UsageRight> {
    return this.http.get<UsageRight>(
      `${AppConfigService.appConfiguration.apiUrl['usageRightServiceOrganization']}/${organizationId}/${bookingId}`
    );
  }

  getUsageRightForCurrentOrgByBookingId(bookingId: string): Observable<UsageRight> {
    return this.http.get<UsageRight>(
      `${AppConfigService.appConfiguration.apiUrl['usageRightServiceOrganization']}/current/${bookingId}`
    );
  }

  updateUsageRight(usageRight: UsageRight): Observable<void> {
    return this.http.put<void>(
      `${AppConfigService.appConfiguration.apiUrl['usageRightServiceUsageRight']}`,
      usageRight
    );
  }

  /**
   * This function returns all booked cartridges of the organization with the organizationID given in the JWT.
   * @return {Observable<CartridgeDto[]>}
   */
  getBookedCartridges(): Observable<CartridgeDto[]> {
    return this.http.get<CartridgeDto[]>(
      `${AppConfigService.appConfiguration.apiUrl['usageRightServiceLeistung']}/booked?sortByStatus=asc`
    );
  }

  getOrganizationWithUsageRightForCartridge(
    cartridgeId: string,
    pageIndex: number,
    pageSize: number
  ): Observable<Page<OrganizationDto>> {
    return this.http.get<Page<OrganizationDto>>(
      `${AppConfigService.appConfiguration.apiUrl['usageRightServiceUsageRight']}/leistung/${cartridgeId}/organization?index=${pageIndex}&size=${pageSize}`
    );
  }

  getOrganizationWithUsageRight(cartridgeId: string, organizationId: string): Observable<OrganizationDetailData> {
    return this.http.get<OrganizationDetailData>(
      `${AppConfigService.appConfiguration.apiUrl['usageRightServiceUsageRight']}/leistung/${cartridgeId}/organization/${organizationId}`
    );
  }

  getActivationState(
    bookingState: BackofficeBookingState | ServiceUpgradeState | BookingState
  ): UsageRightActivationState {
    switch (bookingState) {
      case 'APPROVED':
      case 'BOOKED':
      case 'AUTOMATICALLY_APPROVED':
      case 'TO_BE_CANCELLED':
      case 'UPGRADE_SUGGESTED':
        return UsageRightActivationState.ACTIVE;
      case 'PENDING':
        return UsageRightActivationState.PENDING;
      case 'DECLINED':
        return UsageRightActivationState.DECLINED;
      case 'ACCEPTED':
        return UsageRightActivationState.ACCEPTED;
      case 'ENDED':
        return UsageRightActivationState.ENDED;
      case 'CANCELLED':
        return UsageRightActivationState.CANCELLED;
      case 'UPGRADED':
        return UsageRightActivationState.UPGRADED;
      case 'UPGRADE_REQUEST_OPEN':
      case 'UPGRADE_REQUEST_APPROVED':
        return UsageRightActivationState.UPGRADE_IN_PROGRESS;
      default:
        return UsageRightActivationState.INACTIVE;
    }
  }
}
